import React, { useEffect, useRef } from "react";
import { Flex, Image, Slider, SliderFilledTrack, SliderThumb, SliderTrack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { MIN_CARD_WIDTH, MAX_CARD_WIDTH, CARD_WIDTH_STEP, tileScaleFromCardWidth } from "./seatLayout";
import { tabItemLimitFromScale, useClassroomLayoutStore } from "@/stores/v2/classroomLayoutStore";
import { useSaveTileWidth } from "./useSaveSeatLayout";
import tileSizeSmallIcon from "@/assets/TileSizeIcons/IconTileSizeSmall.svg?url";
import tileSizeLargeIcon from "@/assets/TileSizeIcons/IconTileSizeLarge.svg?url";

interface Props {
	setTabItemLimit?: (limit: number) => void;
}

/**
 * Card-size slider for the seat plan view. Mirrors the default-view tile slider
 * (FlexibleGrid) visually and sits in the same spot on the grid bar, but drives the
 * seat plan's cardWidth instead of the screen-tile scale.
 */
export function SeatCardSizeSlider(props: Props): JSX.Element {
	const { t } = useTranslation();
	const cardWidth = useSeatPlanStore((s) => s.cardWidth);
	const setCardWidth = useSeatPlanStore((s) => s.setCardWidth);
	const setTileScale = useClassroomLayoutStore((s) => s.setTileScale);
	const lastLimitRef = useRef<number | null>(null);

	const teacherId = useUserSessionStore().getTeacherId();
	const classroomId = useSelectedClassroomStore((s) => s.stableIdV1);
	const saveTileWidth = useSaveTileWidth(teacherId, classroomId);

	// Keep the shared tileScale in sync with the seat plan slider while in this view, and
	// emit the matching tab-item cap. Mirrors FlexibleGrid's effect for the default view.
	useEffect(() => {
		const scale = tileScaleFromCardWidth(cardWidth);
		setTileScale(scale);
		const limit = tabItemLimitFromScale(scale);
		if (lastLimitRef.current !== limit) {
			lastLimitRef.current = limit;
			props.setTabItemLimit?.(limit);
		}
	}, [cardWidth, setTileScale, props]);

	const handleCommit = (next: number) => {
		saveTileWidth.mutate({ tileWidth: next }, { onError: (e) => console.warn("Failed to persist seat plan tile width", e) });
	};

	return (
		<Flex data-testid="seatplan-card-size-slider" className="Grid_slider" alignItems="center" gap="8px" width="160px">
			<Image src={tileSizeSmallIcon} alt="" boxSize="14px" />
			<Slider
				data-testid="seatplan-card-size-slider-input"
				aria-label={t("seatPlan.cardSize")}
				value={cardWidth}
				min={MIN_CARD_WIDTH}
				max={MAX_CARD_WIDTH}
				step={CARD_WIDTH_STEP}
				onChange={setCardWidth}
				onChangeEnd={handleCommit}
				focusThumbOnChange={false}
				flex="1"
			>
				<SliderTrack bg="gray.400">
					<SliderFilledTrack />
				</SliderTrack>
				<SliderThumb />
			</Slider>
			<Image src={tileSizeLargeIcon} alt="" boxSize="14px" />
		</Flex>
	);
}
